import { useEffect, useRef, useState } from "react";
import api from "./api";

/**
 * Debounced cross-device auto-save for in-progress measurements.
 * PATCH /api/orders/{orderId}/measurements/draft?token=... whenever `items` change.
 *
 * Returns { saving, savedAt } for a "Saved · just now" indicator.
 */
export default function useMeasurementsAutosave({ orderId, token, items, enabled, delay = 600 }) {
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const timer = useRef(null);
  const firstRun = useRef(true);

  useEffect(() => {
    if (!enabled || !orderId || !token) return undefined;
    // Skip the very first render — we just hydrated from the server.
    if (firstRun.current) {
      firstRun.current = false;
      return undefined;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const payload = {
          items: (items || []).map((it) => ({
            width: it.width === "" || it.width == null ? null : Number(it.width),
            height: it.height === "" || it.height == null ? null : Number(it.height),
            unit: "in",
            frame_color: it.frame_color || null,
            notes: it.notes || "",
            photo_path: it.photo_path || null,
            photo_url: it.photo_url || null,
            petscreen: !!it.petscreen,
            spring_side: it.spring_side || null,
            has_springs: it.has_springs !== false,
            has_pull_tabs: it.has_pull_tabs !== false,
            handle_side: it.handle_side || null,
          })),
        };
        const r = await api.patch(
          `/orders/${orderId}/measurements/draft?token=${encodeURIComponent(token)}`,
          payload,
        );
        setSavedAt(r.data?.saved_at || new Date().toISOString());
      } catch {
        // Silent — autosave shouldn't surface errors to the customer.
      } finally {
        setSaving(false);
      }
    }, delay);
    return () => timer.current && clearTimeout(timer.current);
  }, [orderId, token, enabled, items, delay]);

  return { saving, savedAt };
}
